"use client";

import {
  DecisionMetadata,
  outputId,
  recordOrigin,
  ReferralWorkflowTimeline,
  stepStatus,
  type ReferralWorkflowStep,
} from "@/configuration/groups/confluent-all-star/components/referral-timeline";
import type { ConfluentCarePlanTaskFields } from "@/configuration/groups/confluent-all-star/workflows/care-plan-input";
import { formatUSPhoneNumber } from "@/shared/utils/phone-utils";
import { FieldLayout, type Field } from "@/src/components/cards/common";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { OutboundFaxPdfPreview, outboundFaxPdfPath } from "@/src/components/tasks/document-workflow-steps";
import { WorkflowStepInformationNeeded } from "@/src/components/tasks/workflow-step-information-needed";
import { WorkflowStepResumeButton } from "@/src/components/tasks/workflow-step-resume-button";
import { CARE_PLAN_FAX_NOTE_ID } from "@/src/lib/workflows/steps/ehr/clinicient-chart-note-ids";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { FileType } from "@/src/models/file";
import { TaskStatus, type Task } from "@/src/models/task";
import type { TaskApproval } from "@/src/models/task-approval";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";

const CARE_PLAN_STEP_KEYS = [
  StepKey.RESOLVE_REPLY_FAX,
  StepKey.BUILD_REPLY_FAX,
  StepKey.SEND_REPLY_FAX,
  StepKey.ENSURE_PATIENT,
  StepKey.CLINICIENT_ENSURE_CASE,
  StepKey.CLINICIENT_PATIENT_NOTE,
  StepKey.CLINICIENT_CASE_TASK,
] as const;

type CarePlanStepKey = (typeof CARE_PLAN_STEP_KEYS)[number];

function writtenNote(step: TaskStep | undefined): { noteEhrId: string; note: string } | null {
  const notes = step?.output?.notes;

  if (!Array.isArray(notes)) return null;
  const match = notes.find((note) => note?.id === CARE_PLAN_FAX_NOTE_ID);

  return typeof match?.noteEhrId === "string" && typeof match?.note === "string" ? match : null;
}

export function ConfluentCarePlanSteps({
  task,
  taskSteps,
  pendingApproval,
  dateOfEvent,
  noteText,
  taskFields,
}: {
  task: Task;
  taskSteps: TaskStep[];
  pendingApproval: TaskApproval | null;
  dateOfEvent: string | null;
  noteText: string | null;
  taskFields: ConfluentCarePlanTaskFields;
}) {
  const orderedTaskSteps = taskSteps.toSorted((a, b) => a.stepIndex - b.stepIndex);
  const findStep = (key: StepKey) => orderedTaskSteps.find((step) => step.stepKey === key);
  const routingStep = findStep(StepKey.RESOLVE_REPLY_FAX);
  const buildStep = findStep(StepKey.BUILD_REPLY_FAX);
  const sendStep = findStep(StepKey.SEND_REPLY_FAX);
  const patientStep = findStep(StepKey.ENSURE_PATIENT);
  const caseStep = findStep(StepKey.CLINICIENT_ENSURE_CASE);
  const noteStep = findStep(StepKey.CLINICIENT_PATIENT_NOTE);
  const taskStep = findStep(StepKey.CLINICIENT_CASE_TASK);
  const isSkipped = (step: TaskStep | undefined) => step?.output?.outcome === "skipped";
  const targetFaxNumber = outputId(routingStep, "targetFaxNumber");
  const pageCount = typeof buildStep?.output?.pageCount === "number" ? buildStep.output.pageCount : null;
  const pdfPath = outboundFaxPdfPath(task.id, taskSteps);
  const faxId = outputId(sendStep, "faxId");
  const patientId = outputId(patientStep, "patientEhrId");
  const caseId = outputId(caseStep, "patientCaseEhrId");
  const caseName = outputId(caseStep, "caseName");
  const caseActive = caseStep?.output?.caseActive;
  const note = writtenNote(noteStep);
  const clinicientTaskId = outputId(taskStep, "taskEhrId");
  const lastStep = orderedTaskSteps.at(-1);
  const onHold = task.status === TaskStatus.AWAITING_INFORMATION || task.status === TaskStatus.REVIEW_NEEDED;
  const formattedFax = targetFaxNumber ? (formatUSPhoneNumber(targetFaxNumber) ?? targetFaxNumber) : "Not recorded";

  const caseFields: Field[] = [
    { key: "caseName", label: "Case Name", getValue: () => caseName },
    { key: "dateOfEvent", label: "Plan of Care Date of Event", getValue: () => dateOfEvent },
  ];
  const taskFieldList: Field[] = [
    { key: "action", label: "Action", getValue: () => taskFields.action },
    { key: "dueDate", label: "Due Date", getValue: () => taskFields.dueDate },
    { key: "priority", label: "Priority", getValue: () => taskFields.priority },
    { key: "role", label: "Assign to Role", getValue: () => taskFields.role },
    { key: "note", label: "Note", colSpan: 2, getValue: () => taskFields.note },
    { key: "case", label: "Case", getValue: () => caseName },
  ];

  const definitions: Record<CarePlanStepKey, ReferralWorkflowStep> = {
    [StepKey.RESOLVE_REPLY_FAX]: {
      title: "Find the referring provider's fax number",
      status: stepStatus(
        routingStep,
        isSkipped(routingStep) || targetFaxNumber !== null,
        false,
        isSkipped(routingStep)
      ),
      summary: "Identify the receiving clinic and the referring provider office that should sign the plan of care.",
      content: <DecisionMetadata items={[{ label: "Fax to", value: formattedFax }]} />,
    },
    [StepKey.BUILD_REPLY_FAX]: {
      title: "Generate the fax",
      status: stepStatus(buildStep, isSkipped(buildStep) || pageCount !== null, false, isSkipped(buildStep)),
      summary: "Build the cover sheet and attach the unsigned plan of care.",
      content: (
        <div className="space-y-3">
          <DecisionMetadata items={[{ label: "Pages", value: pageCount ?? "Not recorded" }]} />
          {pdfPath && (
            <div className="h-[900px] overflow-hidden rounded-md border">
              <OutboundFaxPdfPreview taskId={task.id} path={pdfPath} />
            </div>
          )}
        </div>
      ),
    },
    [StepKey.SEND_REPLY_FAX]: {
      title: "Fax the plan of care to the referring provider for signature",
      status: stepStatus(sendStep, isSkipped(sendStep) || faxId !== null, false, isSkipped(sendStep)),
      summary: "Submit the fax to the referring provider's office. Fax submission does not confirm delivery.",
      content: (
        <DecisionMetadata
          items={[
            { label: "Fax recipient", value: formattedFax },
            { label: "Fax record", value: faxId ?? "Not recorded" },
          ]}
        />
      ),
    },
    [StepKey.ENSURE_PATIENT]: {
      title: "Confirm patient chart exists in Clinicient with the following demographic information",
      status: stepStatus(patientStep, patientId !== null),
      summary: "The plan of care is only filed to an existing chart; a new chart is never created.",
      content: (
        <div className="space-y-3">
          {task.patient && <PatientCard patient={task.patient} />}
          <DecisionMetadata
            items={[
              { label: "Clinicient patient ID", value: patientId ?? "Not recorded" },
              { label: "Patient", value: recordOrigin(patientStep?.output?.created) },
            ]}
          />
        </div>
      ),
    },
    [StepKey.CLINICIENT_ENSURE_CASE]: {
      title: "Find the plan of care's case",
      status: stepStatus(caseStep, caseId !== null),
      summary:
        "Attach the plan of care to the patient's existing Clinicient case. A case is created only when the patient has none; cases that cannot be told apart require review.",
      content: (
        <div className="space-y-4 text-sm">
          <div>
            <p className="font-medium">The case is chosen in this order:</p>
            <ol className="mt-1 list-decimal space-y-1 pl-5 text-muted-foreground">
              <li>Active cases are checked first. Inactive cases are checked only when no active case matches.</li>
              <li>
                If the plan of care lists a diagnosis or body part, use the case that matches it the same way referrals
                are matched (discipline, body part, and ICD-10 codes).
              </li>
              <li>
                Otherwise, use the only case; else the only case with a plan of care start date; else the case whose
                plan of care start date is closest to the plan of care&apos;s date of event.
              </li>
              <li>
                If cases are still tied, use the one with the soonest upcoming appointment, then the most recent past
                appointment.
              </li>
              <li>Create a &quot;Default Case&quot; only when the patient has no cases at all.</li>
            </ol>
          </div>
          <FieldLayout hideEmptyFields={false} fields={caseFields} patient={null} />
          <DecisionMetadata
            items={[
              { label: "Clinicient case ID", value: caseId ?? "Not recorded" },
              { label: "Case", value: recordOrigin(caseStep?.output?.created) },
              ...(typeof caseActive === "boolean"
                ? [{ label: "Case status", value: caseActive ? "Active" : "Inactive" }]
                : []),
            ]}
          />
        </div>
      ),
    },
    [StepKey.CLINICIENT_PATIENT_NOTE]: {
      title: "Add plan of care fax note to patient chart",
      status: stepStatus(noteStep, note !== null),
      summary: "To the Notes section of the patient chart, add the following note:",
      content: (
        <div className="space-y-3">
          {(note?.note ?? noteText) && (
            <div className="rounded-md border bg-muted/30 px-3 py-2">
              <CopyableContent text={note?.note ?? noteText ?? ""} alwaysShow />
            </div>
          )}
          <DecisionMetadata items={[{ label: "Clinicient note ID", value: note?.noteEhrId ?? "Not recorded" }]} />
        </div>
      ),
    },
    [StepKey.CLINICIENT_CASE_TASK]: {
      title: "Create the POC Received task",
      status: stepStatus(taskStep, clinicientTaskId !== null),
      summary:
        "Create a task related to the plan of care's case, leaving Assign to Staff blank. Clinic, payer, and referrer come from the case; if the case has no clinic, use the clinic for the fax line the plan of care came in on.",
      content: (
        <div className="space-y-3">
          <FieldLayout hideEmptyFields={false} fields={taskFieldList} patient={null} />
          <DecisionMetadata items={[{ label: "Clinicient task ID", value: clinicientTaskId ?? "Not recorded" }]} />
        </div>
      ),
    },
  };
  const inboundNote = task.files.find((file) => file.type === FileType.INBOUND)?.note;
  const informationNeededStep = onHold ? lastStep : undefined;
  const hasInformationNeededStep = CARE_PLAN_STEP_KEYS.some((key) => key === informationNeededStep?.stepKey);
  const steps: ReferralWorkflowStep[] = CARE_PLAN_STEP_KEYS.map((key) => {
    const step = definitions[key];
    const isBlockingStep = key === lastStep?.stepKey && (onHold || task.status === TaskStatus.FAILED);
    const needsAction = key === informationNeededStep?.stepKey;

    return {
      ...step,
      status: step.status === "failed" ? "failed" : needsAction ? "needs_action" : step.status,
      content: needsAction ? (
        <>
          <WorkflowStepInformationNeeded note={inboundNote} />
          {step.content}
        </>
      ) : (
        step.content
      ),
      continuation:
        isBlockingStep && !pendingApproval && lastStep
          ? {
              instruction: "After fixing the issue, continue from this step. Completed steps will be preserved.",
              control: (
                <WorkflowStepResumeButton key={`${lastStep.id}:${task.updatedAt}`} task={task} step={lastStep} />
              ),
            }
          : undefined,
    };
  });
  const allSynced = steps.every((step) => step.status === "automated" || step.status === "not_needed");
  const isCompleted = task.status === TaskStatus.COMPLETED;
  const isProcessing = task.status === TaskStatus.PROCESSING;
  const hasFailedStep = taskSteps.some((step) => step.status === TaskStepStatus.FAILED);
  const hasTimedHold = taskSteps.some((step) => step.status === TaskStepStatus.PAUSED);
  const hasAsyncStep = taskSteps.some((step) => step.status === TaskStepStatus.ASYNC);
  const workflowNeedsAttention = onHold || hasFailedStep;
  const workflowContinuing = isProcessing || hasAsyncStep || hasTimedHold;
  const canVerify =
    task.status === TaskStatus.VERIFICATION_NEEDED &&
    allSynced &&
    !pendingApproval &&
    !workflowNeedsAttention &&
    !hasAsyncStep &&
    !hasTimedHold;
  const needsAttention =
    workflowNeedsAttention ||
    Boolean(pendingApproval) ||
    steps.some((step) => step.status === "needs_action" || step.status === "failed");

  steps.push({
    title: "Verify in Clinicient",
    status: isCompleted
      ? "verified"
      : hasFailedStep
        ? "failed"
        : workflowNeedsAttention || pendingApproval || canVerify
          ? "needs_action"
          : "waiting",
    summary: isCompleted
      ? "This task has been marked complete."
      : pendingApproval
        ? "Complete the pending workflow confirmation before verifying this task."
        : workflowNeedsAttention
          ? "Resolve the outstanding workflow issue before verifying this task."
          : workflowContinuing
            ? "The workflow is continuing. Verify the results when it finishes."
            : !allSynced
              ? "Resolve the incomplete sync steps above before verifying this task."
              : "Confirm the chart note and POC Received task in Clinicient.",
    content: (
      <p className="text-sm">
        Verify the chart note and POC Received task in Clinicient, then use Correct or Incorrect at the bottom to
        complete this task.
      </p>
    ),
  });

  return (
    <ReferralWorkflowTimeline
      steps={steps}
      isProcessing={workflowContinuing}
      waitingMessage={
        !isCompleted && taskSteps.length === 0 && !needsAttention
          ? "No workflow steps have been recorded for this task yet."
          : undefined
      }
      summaryContent={
        needsAttention && inboundNote && !hasInformationNeededStep ? (
          <p className="whitespace-pre-wrap text-sm">{inboundNote}</p>
        ) : undefined
      }
    />
  );
}
